import { DaoModel } from '@dynameat/models';
import i18n from '@core/plugins/i18n';
import { ForecastGroup } from '../../../models';
import { FrontMenuProductDynamicData } from './frontMenuProductDynamicData';

export class FrontMenuProductDynamicDataList extends DaoModel {
  constructor(saleRateGuid, startDate, endDate, groupList, menuProductDynamicDataList) {
    super(null);
    this.saleRateGuid = saleRateGuid || '';
    this.startDate = startDate || null;
    this.endDate = endDate || null;
    this.groupList = groupList || [];
    this.menuProductDynamicDataList = menuProductDynamicDataList || [];
  }

  static toInstance(props = {}) {
    const groupList = ForecastGroup.toInstanceList(props.group_list);
    const menuProductDynamicDataList = FrontMenuProductDynamicData
      .toInstanceList(props.menu_product_dynamic_data_list);
    return new FrontMenuProductDynamicDataList(props.sale_rate_guid, props.start_date,
      props.end_date, groupList, menuProductDynamicDataList);
  }

  serialize() {
    return {
      sale_rate_guid: this.saleRateGuid,
      start_date: this.startDate,
      end_date: this.endDate,
      group_list: this.groupList.map((group) => group.serialize()),
      menu_product_dynamic_data_list:
        this.menuProductDynamicDataList.map((mpdd) => mpdd.serialize())
    };
  }

  editOrder({ mpddGuid, newOrder }) {
    this.menuProductDynamicDataList.find((mpdd) => mpdd.guid === mpddGuid)?.changeOrder(newOrder);
  }

  editSpecial({ mpddGuid, newValue }) {
    this.menuProductDynamicDataList.find((mpdd) => mpdd.guid === mpddGuid)?.changeSpecial(newValue);
  }

  editIsVisible({ mpddGuid, newValue }) {
    this.menuProductDynamicDataList.find((mpdd) => mpdd.guid === mpddGuid)?.changeIsVisible(newValue);
  }

  toTable(services, mainColumnProps, childColumnProps, initialColumn) {
    const rows = [];
    const columns = [];
    const days = new Set();
    const serviceGuids = new Set();

    this.#getRows(rows, days, serviceGuids);
    this.#getColumns(columns, days, serviceGuids, services, mainColumnProps, childColumnProps, initialColumn);
    return { rows, columns };
  }

  #getRows(rows, days, serviceGuids) {
    let currentRow = null;
    let currentProduct = null;
    let mpdd;
    for (let i = 0; i < this.menuProductDynamicDataList.length; i++) {
      mpdd = this.menuProductDynamicDataList[i];
      days.add(mpdd.date);
      serviceGuids.add(mpdd.serviceGuid);
      if (currentProduct !== mpdd.menuProductGuid) {
        if (currentRow) {
          rows.push(currentRow);
        }
        currentRow = {
          productName: mpdd.productName,
          menuProductGuid: mpdd.menuProductGuid,
          productGuid: mpdd.productGuid
        };
        currentProduct = mpdd.menuProductGuid;
      }
      FrontMenuProductDynamicDataList.#processCell(currentRow, mpdd);
    }
    if (currentRow) {
      rows.push(currentRow);
    }
  }

  static getDateString(dayString) {
    return i18n.d(new Date(dayString), 'long');
  }

  #getColumns(columns, days, serviceGuids, services, mainColumnProps, childColumnProps, initialColumn) {
    const servicesMap = Object.fromEntries(services.map((service) => ([service.guid, service])));
    if (initialColumn) {
      columns.push(initialColumn);
    }
    columns.push(...[...days].map((day) => ({
      name: FrontMenuProductDynamicDataList.getDateString(day),
      ...mainColumnProps,
      children: [...serviceGuids].map((serviceGuid) => ({
        name: servicesMap[serviceGuid].name,
        prop: `${day}_${serviceGuid}`,
        ...childColumnProps,
      }))
    })));
  }

  static #processCell(currentRow, mpdd) {
    const keyOrder = `${mpdd.date}_${mpdd.serviceGuid}`;
    const keySpecial = `${mpdd.date}_${mpdd.serviceGuid}_special`;
    const keyIsVisible = `${mpdd.date}_${mpdd.serviceGuid}_is_visible`;
    const keyGuid = `${mpdd.date}_${mpdd.serviceGuid}_guid`;
    currentRow[keyOrder] = mpdd.order;
    currentRow[keySpecial] = mpdd.special;
    currentRow[keyIsVisible] = mpdd.isVisible;
    currentRow[keyGuid] = mpdd.guid;
  }

  getChangedList() {
    const changedMpddList = this.menuProductDynamicDataList.filter((el) => el.hasChanged());
    return new FrontMenuProductDynamicDataList(this.saleRateGuid, this.startDate, this.endDate, this.groupList, changedMpddList);
  }

  getByMpddGuid(mpddGuid) {
    return this.menuProductDynamicDataList.find((el) => el.guid === mpddGuid);
  }

  hasMpddChanged(mpddGuid, newOrder, newSpecial, newIsVisible) {
    const mpdd = this.getByMpddGuid(mpddGuid);
    return newSpecial !== mpdd.special || newOrder !== mpdd.order || newIsVisible !== mpdd.isVisible;
  }
}
